import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../utils/firebase';
import { collection, getDocs, addDoc, collectionGroup, query, limit, startAfter, orderBy, serverTimestamp } from 'firebase/firestore';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BottomTabBar from '../components/BottomTabBar';
import LeaderboardSection from '../components/LeaderboardSection';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import '../styles/user.css';
import '../styles/data-table.css';
import '../styles/buttons.css';
import '../styles/community.css';

import logger from '../utils/logger';
import { getDailyWeeklyMonthlyRanges } from '../utils/dateRanges';
import { toMillis } from '../utils/timestamps';
import { calculateCaloriesBurned } from '../utils/calories';
import { formatActivityDetails } from '../utils/activities';
import { calculateStreak } from '../utils/streaks';
import { useToast } from '../context/ToastContext';

const PAGE_SIZE = 15;

function Community() {
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useToast();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardMinsData, setLeaderboardMinsData] = useState([]);
  const [leaderboardCaloriesData, setLeaderboardCaloriesData] = useState([]);
  const [leaderboardStreakData, setLeaderboardStreakData] = useState({ entries: [], max: 1 });
  const [recentActivities, setRecentActivities] = useState([]);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreReps, setHasMoreReps] = useState(true);
  const [lastRepDoc, setLastRepDoc] = useState(null);

  // Fix 3: Use auth.currentUser directly instead of onAuthStateChanged listener.
  // Auth is already gated at the App level — this component only renders when
  // the user is authenticated.
  const currentUserId = auth.currentUser?.uid;

  // Accumulated data across pages — stored in refs to avoid stale closure issues
  const userMapRef = useRef({});
  const allRepsRef = useRef([]);

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  const renderMessageWithLinks = (message) => {
    const parts = message.split(urlRegex);
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#f97316', textDecoration: 'none' }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // Runs the existing leaderboard aggregation on the full accumulated reps array
  // and updates all leaderboard + recent activities state.
  const processRepsData = (repsData, userMap) => {
    const { todayStart, weekStart, weekEnd, monthStart, monthEnd } = getDailyWeeklyMonthlyRanges();

    const dailyTotals = {};
    const weeklyTotals = {};
    const monthlyTotals = {};

    const dailyMinsTotals = {};
    const weeklyMinsTotals = {};
    const monthlyMinsTotals = {};

    const dailyCaloriesTotals = {};
    const weeklyCaloriesTotals = {};
    const monthlyCaloriesTotals = {};

    repsData.forEach(rep => {
      const user = userMap[rep.userId] || { displayName: 'Unknown', hideExerciseInfo: false, weight: null };
      if (user.hideExerciseInfo) return;
      const userName = user.displayName;
      const activityType = rep.activityType || rep.type;
      if (!activityType) return;
      const count = rep.count || rep.reps || 0;
      const time = rep.time || 0;
      const calories = calculateCaloriesBurned({ ...rep, activityType }, user.weight);

      if (toMillis(rep.timestamp) >= todayStart) {
        dailyTotals[userName] = (dailyTotals[userName] || 0) + count;
        dailyMinsTotals[userName] = (dailyMinsTotals[userName] || 0) + time;
        dailyCaloriesTotals[userName] = (dailyCaloriesTotals[userName] || 0) + calories;
      }

      if (toMillis(rep.timestamp) >= weekStart && toMillis(rep.timestamp) <= weekEnd) {
        weeklyTotals[userName] = (weeklyTotals[userName] || 0) + count;
        weeklyMinsTotals[userName] = (weeklyMinsTotals[userName] || 0) + time;
        weeklyCaloriesTotals[userName] = (weeklyCaloriesTotals[userName] || 0) + calories;
      }

      if (toMillis(rep.timestamp) >= monthStart && toMillis(rep.timestamp) <= monthEnd) {
        monthlyTotals[userName] = (monthlyTotals[userName] || 0) + count;
        monthlyMinsTotals[userName] = (monthlyMinsTotals[userName] || 0) + time;
        monthlyCaloriesTotals[userName] = (monthlyCaloriesTotals[userName] || 0) + calories;
      }
    });

    const sortTotals = (totals) => {
      const entries = Object.entries(totals)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 3);
      const max = entries.length > 0 ? entries[0].total : 1;
      return { entries, max };
    };

    setLeaderboardData({
      daily: sortTotals(dailyTotals),
      weekly: sortTotals(weeklyTotals),
      monthly: sortTotals(monthlyTotals)
    });

    setLeaderboardMinsData({
      daily: sortTotals(dailyMinsTotals),
      weekly: sortTotals(weeklyMinsTotals),
      monthly: sortTotals(monthlyMinsTotals)
    });

    setLeaderboardCaloriesData({
      daily: sortTotals(dailyCaloriesTotals),
      weekly: sortTotals(weeklyCaloriesTotals),
      monthly: sortTotals(monthlyCaloriesTotals)
    });

    // Streak leaderboard
    const repsByUser = {};
    repsData.forEach(rep => {
      const user = userMap[rep.userId];
      if (!user || user.hideExerciseInfo) return;
      if (!repsByUser[user.displayName]) repsByUser[user.displayName] = [];
      repsByUser[user.displayName].push(rep);
    });
    const streakEntries = Object.entries(repsByUser)
      .map(([name, reps]) => {
        const { currentStreak } = calculateStreak(reps);
        return { name, total: currentStreak };
      })
      .filter(e => e.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);
    const streakMax = streakEntries.length > 0 ? streakEntries[0].total : 1;
    setLeaderboardStreakData({ entries: streakEntries, max: streakMax });

    const sortedReps = repsData
      .filter(rep => {
        const user = userMap[rep.userId] || { hideExerciseInfo: false };
        const activityType = rep.activityType || rep.type;
        return activityType && !user.hideExerciseInfo;
      })
      .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp))
      .slice(0, 15)
      .map(rep => {
        const activityType = (rep.activityType || rep.type).replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
        return {
          user: userMap[rep.userId]?.displayName || 'Unknown',
          activity: activityType,
          details: formatActivityDetails({ count: rep.count || rep.reps || 0, time: rep.time || 0, activityType }),
          date: new Date(toMillis(rep.timestamp)).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
        };
      });
    setRecentActivities(sortedReps);
  };

  // Fix 2: Batch user profile lookups in parallel instead of sequential awaits.
  const fetchRepsPage = async (cursor) => {
    const q = cursor
      ? query(collectionGroup(db, 'reps'), orderBy('timestamp', 'desc'), limit(PAGE_SIZE), startAfter(cursor))
      : query(collectionGroup(db, 'reps'), orderBy('timestamp', 'desc'), limit(PAGE_SIZE));

    const snapshot = await getDocs(q);

    const newReps = snapshot.docs.map(d => {
      const pathSegments = d.ref.path.split('/');
      const userId = pathSegments[0] === 'reps' ? d.data().userId : d.ref.parent.parent.id;
      return { id: d.id, userId, ...d.data() };
    });

    // Collect unseen userIds and fetch all profiles in parallel
    const unseenIds = [...new Set(newReps.map(r => r.userId))].filter(id => !userMapRef.current[id]);
    if (unseenIds.length > 0) {
      const profiles = await Promise.all(
        unseenIds.map(id => getDoc(doc(db, 'users', id)))
      );
      profiles.forEach((userDoc, i) => {
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userMapRef.current[unseenIds[i]] = {
            displayName: userData.displayName || 'Unknown',
            hideExerciseInfo: userData.hideExerciseInfo || false,
            weight: userData.currentWeight || 155,
          };
        } else {
          userMapRef.current[unseenIds[i]] = { displayName: 'Unknown', hideExerciseInfo: false, weight: 155 };
        }
      });
    }

    allRepsRef.current = [...allRepsRef.current, ...newReps];

    const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;
    setLastRepDoc(lastDoc);
    setHasMoreReps(snapshot.docs.length === PAGE_SIZE);

    processRepsData(allRepsRef.current, userMapRef.current);
  };

  // Fix 1: Parallelize reps and posts fetch with Promise.all().
  // Fix 1 cont: Batch post author lookups in parallel.
  useEffect(() => {
    if (!currentUserId) return;

    // Reset accumulated state before fetching fresh data
    userMapRef.current = {};
    allRepsRef.current = [];
    setLastRepDoc(null);
    setHasMoreReps(true);

    const fetchPosts = async () => {
      const postsSnapshot = await getDocs(collectionGroup(db, 'posts'));
      const tempPostsData = postsSnapshot.docs.map(d => {
        const pathSegments = d.ref.path.split('/');
        let userId;
        if (pathSegments[0] === 'posts') {
          userId = d.data().userId;
        } else {
          userId = d.ref.parent.parent.id;
        }
        return { id: d.id, userId, ...d.data() };
      });

      // Batch author lookups in parallel
      const authorIds = [...new Set(tempPostsData.map(p => p.userId))];
      const authorDocs = await Promise.all(
        authorIds.map(id => getDoc(doc(db, 'users', id)))
      );
      const authorMap = {};
      authorDocs.forEach((d, i) => {
        authorMap[authorIds[i]] = d.exists() ? d.data().displayName || 'Unknown' : 'Unknown';
      });

      return tempPostsData
        .map(post => ({ ...post, userName: authorMap[post.userId] || 'Unknown' }))
        .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp))
        .slice(0, 30);
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fire reps and posts fetches in parallel
        const [, postsData] = await Promise.all([
          fetchRepsPage(null),
          fetchPosts()
        ]);
        setPosts(postsData);
      } catch (err) {
        logger.error(err, { page: 'Community', action: 'fetchCommunityData' });
        setError('Failed to fetch community data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUserId, location.state?.refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      await fetchRepsPage(lastRepDoc);
    } catch (err) {
      logger.error(err, { page: 'Community', action: 'loadMoreData' });
      setError('Failed to load more data. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
      setError(null);
    }
  }, [error, showToast]);

  const addPost = async () => {
    if (!newPost.trim()) {
      setError('Post message cannot be empty.');
      return;
    }
    if (!currentUserId) {
      setError('You must be logged in to post.');
      return;
    }
    const nowMs = Date.now();
    const post = { message: newPost };
    try {
      const docRef = await addDoc(collection(db, 'users', currentUserId, 'posts'), {
        ...post,
        timestamp: serverTimestamp(),
      });
      const userDoc = await getDoc(doc(db, 'users', currentUserId));
      const userName = userDoc.exists() ? userDoc.data().displayName || 'Unknown' : 'Unknown';
      setPosts([{ id: docRef.id, userId: currentUserId, userName, ...post, timestamp: nowMs }, ...posts.slice(0, 29)]);
      setNewPost('');
    } catch (error) {
      logger.error(error, { page: 'Community', action: 'addPost' });
      setError('Failed to add post. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addPost();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      logger.error(error, { page: 'Community', action: 'logout' });
      setError('Failed to log out. Please try again.');
    }
  };

  // Streak formatter for the LeaderboardSection component
  const streakFormatter = (entry) => `🔥 ${entry.total}-day streak`;

  return (
    <div className="Community" style={{ paddingBottom: '80px' }}>
      <nav className="nav-desktop">
        <Link to="/user" className="nav-brand">FriendsFitness</Link>
        <div className="nav-links-right">
          <Link to="/user" className={`nav-pill${location.pathname === '/user' ? ' active-link' : ''}`}>User</Link>
          <Link to="/stats" className={`nav-pill${location.pathname === '/stats' ? ' active-link' : ''}`}>Stats</Link>
          <Link to="/community" className={`nav-pill${location.pathname === '/community' ? ' active-link' : ''}`}>Community</Link>
          <Link to="/account" className={`nav-pill${location.pathname === '/account' ? ' active-link' : ''}`}>Account</Link>
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </div>
      </nav>
      <BottomTabBar />

      <div className="posts-section">
        <h3>Community Posts</h3>
        <div className="post-input-container">
          <input
            type="text"
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
          />
          <button className="btn-primary" onClick={addPost}>Post</button>
        </div>
        {loading ? (
          <p className="loading">Loading...</p>
        ) : (
          <div className="posts-scroll-container">
            {posts.map(post => (
              <div key={post.id} className="post-container">
                <div className="post-meta">
                  <span>{post.userName}  {new Date(toMillis(post.timestamp)).toLocaleDateString()}</span>
                </div>
                <div className={`post ${post.userId === currentUserId ? 'user-post' : 'other-post'}`}>
                <div className="post-message">
                    {renderMessageWithLinks(post.message)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="recent-activities-section">
        <h3>Recent Activities</h3>
        {loading ? (
          <p className="loading">Loading...</p>
        ) : (
          <div className="data-table data-table--community">
            <div className="data-table-header">
              <span>Date</span>
              <span>User</span>
              <span>Activity</span>
              <span>Details</span>
            </div>
            {recentActivities.map((activity, index) => (
              <div key={index} className="data-table-row">
                <span className="data-table-cell" data-label="Date">{activity.date}</span>
                <span className="data-table-cell" data-label="User">{activity.user}</span>
                <span className="data-table-cell" data-label="Activity">{activity.activity}</span>
                <span className="data-table-cell" data-label="Details">{activity.details}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fix 4: Extracted LeaderboardSection component replaces ~300 lines of repeated JSX */}
      <LeaderboardSection title="Reps" data={leaderboardData} loading={loading} />
      <LeaderboardSection title="Minutes" data={leaderboardMinsData} loading={loading} />
      <LeaderboardSection title="Calories" data={leaderboardCaloriesData} loading={loading} />
      <LeaderboardSection title="Streaks" data={leaderboardStreakData} loading={loading} formatter={streakFormatter} />

      {!loading && (
        <div className="leaderboard-load-more">
          {loadingMore ? (
            <p className="loading">Loading more data...</p>
          ) : hasMoreReps ? (
            <button className="load-more-link" onClick={handleLoadMore}>
              Load more
            </button>
          ) : (
            <p className="loading">All data loaded.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Community;
